import React, { useEffect, useMemo, useState } from "react";
import Modal from "../../components/Modal";
import { VaccineFormData, VaccineSchedule } from "../../interfaces/vaccines";
import { createVaccine, updateVaccine } from "../../apis/vaccines";

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editableId?: number | null;
  vaccineData?: VaccineFormData | null;
}

const emptySchedule = (): VaccineSchedule => ({
  doseNumber: 1,
  recommendedAgeDays: null,
  recommendedAgeMonths: null,
  recommendedAgeYears: null,
  minimumAgeDays: null,
  minimumIntervalDays: null,
  isBooster: false,
  notes: "",
});

const AddVaccineModal: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
  editableId,
  vaccineData,
}) => {
  const defaultVaccine: VaccineFormData = useMemo(
    () => ({
      name: "",
      shortName: "",
      description: "",
      manufacturer: "",
      vaccineType: "",
      route: "",
      site: "",
      isActive: true,
      schedules: [emptySchedule()],
    }),
    []
  );

  const [vaccine, setVaccine] = useState<VaccineFormData>(defaultVaccine);
  const [errors, setErrors] = useState<{ name?: string }>({});
  const [saving, setSaving] = useState(false);
  const isEditMode = Boolean(editableId);

  useEffect(() => {
    if (!open) return;
    if (isEditMode && vaccineData) {
      setVaccine({
        name: vaccineData.name ?? "",
        shortName: vaccineData.shortName ?? "",
        description: vaccineData.description ?? "",
        manufacturer: vaccineData.manufacturer ?? "",
        vaccineType: vaccineData.vaccineType ?? "",
        route: vaccineData.route ?? "",
        site: vaccineData.site ?? "",
        isActive: vaccineData.isActive ?? true,
        schedules:
          vaccineData.schedules && vaccineData.schedules.length > 0
            ? vaccineData.schedules
            : [emptySchedule()],
      });
    } else {
      setVaccine(defaultVaccine);
    }
    setErrors({});
  }, [open, isEditMode, vaccineData, defaultVaccine]);

  const updateSchedule = (index: number, patch: Partial<VaccineSchedule>) => {
    const schedules = [...(vaccine.schedules || [])];
    schedules[index] = { ...schedules[index], ...patch };
    setVaccine({ ...vaccine, schedules });
  };

  const addSchedule = () => {
    const nextDose = (vaccine.schedules?.length || 0) + 1;
    setVaccine({
      ...vaccine,
      schedules: [
        ...(vaccine.schedules || []),
        { ...emptySchedule(), doseNumber: nextDose },
      ],
    });
  };

  const removeSchedule = (index: number) => {
    const schedules = (vaccine.schedules || []).filter((_, i) => i !== index);
    setVaccine({ ...vaccine, schedules });
  };

  const validateForm = () => {
    const e: typeof errors = {};
    if (!vaccine.name?.trim()) e.name = "* Name is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    const payload: VaccineFormData = {
      name: vaccine.name.trim(),
      shortName: vaccine.shortName?.trim() || null,
      description: vaccine.description?.trim() || null,
      manufacturer: vaccine.manufacturer?.trim() || null,
      vaccineType: vaccine.vaccineType?.trim() || null,
      route: vaccine.route?.trim() || null,
      site: vaccine.site?.trim() || null,
      isActive: vaccine.isActive ?? true,
      schedules: (vaccine.schedules || []).map((s, idx) => ({
        id: s.id,
        doseNumber: Number(s.doseNumber) || idx + 1,
        recommendedAgeDays: s.recommendedAgeDays ?? null,
        recommendedAgeMonths: s.recommendedAgeMonths ?? null,
        recommendedAgeYears: s.recommendedAgeYears ?? null,
        minimumAgeDays: s.minimumAgeDays ?? null,
        minimumIntervalDays: s.minimumIntervalDays ?? null,
        isBooster: !!s.isBooster,
        notes: s.notes?.trim() || null,
      })),
    };
    try {
      if (isEditMode) {
        await updateVaccine(editableId!, payload);
      } else {
        await createVaccine(payload);
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.log("Error saving vaccine:", error);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Modal
      title={isEditMode ? "Edit Vaccine" : "Add Vaccine"}
      onClose={onClose}
      width="max-w-3xl"
      modalContent={
        <div className="p-4 space-y-4 max-h-[75vh] overflow-y-auto">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                className="block mt-1 w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                value={vaccine.name}
                onChange={(e) => setVaccine({ ...vaccine, name: e.target.value })}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-500">{errors.name}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Short Name
              </label>
              <input
                className="block mt-1 w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                value={vaccine.shortName || ""}
                onChange={(e) =>
                  setVaccine({ ...vaccine, shortName: e.target.value })
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Manufacturer
              </label>
              <input
                className="block mt-1 w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                value={vaccine.manufacturer || ""}
                onChange={(e) =>
                  setVaccine({ ...vaccine, manufacturer: e.target.value })
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Type
              </label>
              <input
                className="block mt-1 w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                value={vaccine.vaccineType || ""}
                onChange={(e) =>
                  setVaccine({ ...vaccine, vaccineType: e.target.value })
                }
                placeholder="e.g. Live attenuated"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Route
              </label>
              <input
                className="block mt-1 w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                value={vaccine.route || ""}
                onChange={(e) =>
                  setVaccine({ ...vaccine, route: e.target.value })
                }
                placeholder="e.g. IM, Oral"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Site
              </label>
              <input
                className="block mt-1 w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                value={vaccine.site || ""}
                onChange={(e) =>
                  setVaccine({ ...vaccine, site: e.target.value })
                }
                placeholder="e.g. Left arm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              rows={2}
              className="block mt-1 w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
              value={vaccine.description || ""}
              onChange={(e) =>
                setVaccine({ ...vaccine, description: e.target.value })
              }
            />
          </div>

          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={!!vaccine.isActive}
              onChange={(e) =>
                setVaccine({ ...vaccine, isActive: e.target.checked })
              }
            />
            Active
          </label>

          <div className="pt-2 border-t border-gray-200">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-semibold text-gray-900">
                Dose Schedules
              </h3>
              <button
                type="button"
                onClick={addSchedule}
                className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
              >
                + Add Dose
              </button>
            </div>
            <div className="space-y-3">
              {(vaccine.schedules || []).map((schedule, index) => (
                <div
                  key={schedule.id ?? `new-${index}`}
                  className="p-3 rounded-md border border-gray-200 bg-gray-50"
                >
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      Dose #{schedule.doseNumber || index + 1}
                    </span>
                    {(vaccine.schedules || []).length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSchedule(index)}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                    <div>
                      <label className="text-xs text-gray-500">Dose No.</label>
                      <input
                        type="number"
                        min={0}
                        className="block mt-1 w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                        value={schedule.doseNumber}
                        onChange={(e) =>
                          updateSchedule(index, {
                            doseNumber: Number(e.target.value),
                          })
                        }
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Age (Years)</label>
                      <input
                        type="number"
                        min={0}
                        className="block mt-1 w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                        value={schedule.recommendedAgeYears ?? ""}
                        onChange={(e) =>
                          updateSchedule(index, {
                            recommendedAgeYears: e.target.value
                              ? Number(e.target.value)
                              : null,
                          })
                        }
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Age (Months)</label>
                      <input
                        type="number"
                        min={0}
                        className="block mt-1 w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                        value={schedule.recommendedAgeMonths ?? ""}
                        onChange={(e) =>
                          updateSchedule(index, {
                            recommendedAgeMonths: e.target.value
                              ? Number(e.target.value)
                              : null,
                          })
                        }
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Age (Days)</label>
                      <input
                        type="number"
                        min={0}
                        className="block mt-1 w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                        value={schedule.recommendedAgeDays ?? ""}
                        onChange={(e) =>
                          updateSchedule(index, {
                            recommendedAgeDays: e.target.value
                              ? Number(e.target.value)
                              : null,
                          })
                        }
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Min Age (Days)</label>
                      <input
                        type="number"
                        min={0}
                        className="block mt-1 w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                        value={schedule.minimumAgeDays ?? ""}
                        onChange={(e) =>
                          updateSchedule(index, {
                            minimumAgeDays: e.target.value
                              ? Number(e.target.value)
                              : null,
                          })
                        }
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">
                        Min Interval (Days)
                      </label>
                      <input
                        type="number"
                        min={0}
                        className="block mt-1 w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                        value={schedule.minimumIntervalDays ?? ""}
                        onChange={(e) =>
                          updateSchedule(index, {
                            minimumIntervalDays: e.target.value
                              ? Number(e.target.value)
                              : null,
                          })
                        }
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="inline-flex gap-2 items-center text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={!!schedule.isBooster}
                          onChange={(e) =>
                            updateSchedule(index, {
                              isBooster: e.target.checked,
                            })
                          }
                        />
                        Booster
                      </label>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Notes</label>
                      <input
                        className="block mt-1 w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                        value={schedule.notes || ""}
                        onChange={(e) =>
                          updateSchedule(index, { notes: e.target.value })
                        }
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={handleSave}
              className="px-4 py-2 text-sm font-semibold text-white rounded-md bg-primary hover:bg-hover disabled:opacity-50"
            >
              {saving ? "Saving..." : isEditMode ? "Update" : "Save"}
            </button>
          </div>
        </div>
      }
    />
  );
};

export default AddVaccineModal;
